import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type { Numeric } from '@/utils/numeric';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConditionMetric = 'value' | 'tank_liters' | 'tank_percent';
export type ConditionOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq';

export interface LeafCondition {
  metric: ConditionMetric;
  op: ConditionOp;
  value: number;
}
export interface AllCondition {
  all: RuleCondition[];
}
export interface AnyCondition {
  any: RuleCondition[];
}
export type RuleCondition = LeafCondition | AllCondition | AnyCondition;

export type RecipientSpec = number[] | 'all_admins';

export interface NotifyInAppAction {
  type: 'notify_in_app';
  user_ids: RecipientSpec;
  title?: string;
  body?: string;
}
export interface NotifyEmailAction {
  type: 'notify_email';
  user_ids: RecipientSpec;
  subject?: string;
  body?: string;
}
export interface RelayOnAction {
  type: 'relay_on';
  controller_id: string;
  router_mac: string;
  relay_index: number;
  duration_sec: number;
}
export interface RelayOffAction {
  type: 'relay_off';
  controller_id: string;
  router_mac: string;
  relay_index: number;
}
export type RuleAction =
  | NotifyInAppAction
  | NotifyEmailAction
  | RelayOnAction
  | RelayOffAction;

export interface SensorRule {
  id: number;
  organisation_id: number;
  device_id: string;
  channel_index: number;
  channel_kind: 'analog' | 'digital';
  name: string;
  enabled: boolean;
  condition: RuleCondition;
  actions: RuleAction[];
  cooldown_sec: number;
  hysteresis: RuleCondition | null;
  last_state: boolean;
  last_fired_at: string | null;
  last_error: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface RuleEvent {
  id: number;
  rule_id: number;
  fired_at: string;
  trigger_value: Numeric;
  actions_run: Array<{ type: string; ok: boolean; error?: string; detail?: unknown }>;
  note: string | null;
}

export interface RuleTestResult {
  latest_value: number | null;
  metrics: {
    value: number | null;
    tank_liters?: number | null;
    tank_percent?: number | null;
  };
  primary_true: boolean;
  disarm_true: boolean;
  would_fire: boolean;
  current_state: boolean;
}

export interface CreateRuleInput {
  device_id: string;
  channel_index: number;
  channel_kind: 'analog' | 'digital';
  name: string;
  enabled?: boolean;
  condition: RuleCondition;
  actions: RuleAction[];
  cooldown_sec?: number;
  hysteresis?: RuleCondition | null;
}

export type UpdateRuleInput = Partial<Omit<CreateRuleInput, 'device_id' | 'channel_index' | 'channel_kind'>>;

// ─── API ──────────────────────────────────────────────────────────────────────

async function listRules(params: {
  device_id?: string;
  channel_index?: number;
  channel_kind?: string;
} = {}): Promise<SensorRule[]> {
  const qs = new URLSearchParams();
  if (params.device_id) qs.set('device_id', params.device_id);
  if (params.channel_index !== undefined) qs.set('channel_index', String(params.channel_index));
  if (params.channel_kind) qs.set('channel_kind', params.channel_kind);
  const tail = qs.toString() ? `?${qs.toString()}` : '';
  const res = await apiService.get<SensorRule[]>(`/sensor-rules${tail}`);
  return unwrap(res);
}

async function getRule(id: number): Promise<SensorRule> {
  const res = await apiService.get<SensorRule>(`/sensor-rules/${id}`);
  return unwrap(res);
}

async function createRule(input: CreateRuleInput): Promise<SensorRule> {
  const res = await apiService.post<SensorRule>('/sensor-rules', input);
  return unwrap(res);
}

async function updateRule(id: number, input: UpdateRuleInput): Promise<SensorRule> {
  const res = await apiService.put<SensorRule>(`/sensor-rules/${id}`, input);
  return unwrap(res);
}

async function deleteRule(id: number): Promise<void> {
  await apiService.delete(`/sensor-rules/${id}`);
}

async function testRule(id: number): Promise<RuleTestResult> {
  const res = await apiService.post<RuleTestResult>(`/sensor-rules/${id}/test`, {});
  return unwrap(res);
}

async function listEvents(id: number, limit = 50): Promise<RuleEvent[]> {
  const res = await apiService.get<RuleEvent[]>(`/sensor-rules/${id}/events?limit=${limit}`);
  return unwrap(res);
}

export default {
  listRules,
  getRule,
  createRule,
  updateRule,
  deleteRule,
  testRule,
  listEvents,
};
